<template>
    <div
        class="border border-primary-20 rounded-4 w-full"
        :class="{'hover:bg-primary-5': !campaign.brockmeyer_old_partner}"
        @mouseover="fetchJob"
    >
        <div class="flex justify-between items-stretch">
            <div
                class="w-full my-auto p-16"
                :class="{'cursor-pointer': !campaign.brockmeyer_old_partner}"
                @click="editCampaign"
            >
                <div class="flex items-start mb-16">
                    <p class="text-body font-semibold mr-16">{{ name }}</p>
                    <tooltip
                        :content="campaign.cancelled_reason"
                        :disabled="campaign.status !== 'cancelled' || !campaign.cancelled_reason"
                    >
                        <badge
                            class="shrink-0 cursor-default"
                            :display="statusColor"
                            :content="status"
                        />
                    </tooltip>
                    <badge
                        v-if="campaign.brockmeyer_old_partner"
                        :content="$t('campaigns.archived')"
                        class="cursor-default"
                    />
                </div>
                <div class="flex gap-24">
                    <p>{{ this.campaign.draft_mode ? createdAt : startDate }}</p>
                    <p class="inline-flex items-center" v-if="price(this.campaign) > 0">
                        <currency/>
                        {{ price(this.campaign).toFixed(2) }}
                    </p>
                    <p v-else>{{ $t('multi-posting.price.free') }}</p>
                    <div class="flex justify-center items-center gap-4">
                        <svg-icon type="calendar-today" class="-mt-1" />
                        <p>{{ duration }} {{ $tc('general.days', duration) }}</p>
                    </div>
                    <div v-if="campaign.brockmeyer_old_partner" class="flex justify-center items-center gap-4">
                        <svg-icon type="clicks" class="-mt-1" />
                        <p>{{ campaign.extra['clicks'] ?? 0 }} <span class="lowercase">{{ $tc('general.clicks', campaign.extra['clicks'] ?? 0 ) }}</span></p>
                    </div>
                    <p v-if="campaign.brockmeyer_old_partner">#{{ campaign.brockmeyer_campaign_id}}</p>
                </div>
            </div>
            <div class="flex items-center p-16">
                <div class="flex flex-col items-center mr-16">
                    <div class="flex justify-center items-center bg-primary-20 rounded-4 w-56 h-36 mb-4">
                        <span class="text-body font-semibold text-primary-80">{{ applications }}</span>
                    </div>
                    <p class="text-body-sm font-semibold">{{ $t('general.applicants') }}</p>
                </div>
                <div class="flex flex-col gap-12 justify-between">
                    <campaign-more-menu
                        v-if="!campaign.brockmeyer_old_partner"
                        :campaign="campaign"
                        @add-new-from-this="addNewFromThis"
                        @view-anayltics="viewAnalytics"
                        @offline-campaign="takingCampaignOffline = true"
                        @delete-draft="deletingDraft = true"
                        @duplicate-campaign="duplicateCampaignAction"
                    />

                    <simple-button
                        display="neutral"
                        :icon="accordionIcon"
                        look="icon"
                        small
                        @click="toggleAccordion"
                    />
                </div>
            </div>
        </div>

        <div
            class="transition-all flex overflow-hidden"
            :class="accordionOpen ? 'max-h-356 border-t' : 'max-h-0 border-t-0 mt-0'"
        >
            <campaign-channels
                :campaign="campaign"
                @offline-channel="takeOffline"
            />
        </div>

        <popup-modal-confirm
            v-if="deletingDraft"
            show-exclamation
            :title="$t('campaigns.delete_draft_confirmation').toString()"
            :working="performingDeletion"
            @close="deletingDraft = false; performingDeletion = false"
            @confirm="deleteDraftCampaign"
        />

        <popup-modal-confirm
            v-if="takingCampaignOffline"
            :message="$t('multi-posting.take_offline.campaign.message').toString()"
            show-exclamation
            :title="$t('multi-posting.take_offline.campaign.title').toString()"
            :toggle-text="$t('general.irrevocable')"
            :working="performingTakeCampaignOffline"
            @close="takingCampaignOffline = false; performingTakeCampaignOffline = false"
            @confirm="takeCampaignOffline"
        />

        <popup-modal-confirm
            v-if="takingChannelOffline"
            :message="$t('multi-posting.take_offline.channel.message').toString()"
            show-exclamation
            :title="$t('multi-posting.take_offline.channel.title').toString()"
            :working="performingTakeChannelOffline"
            @close="takingChannelOffline = false; performingTakeChannelOffline = false"
            @confirm="takeChannelOffline"
        />
    </div>
</template>

<script>
import SvgIcon from "../ui/SvgIcon.vue";
import CampaignChannels from "./CampaignChannels.vue";
import CampaignMoreMenu from './CampaignMoreMenu.vue';
import moment from 'moment';
import Currency from "../Currency.vue";
import PopupModalConfirm from "../ui/PopupModalConfirm.vue";
import Job from "../../models/Job";
import * as JobService from "../../services/JobService";
import { ADD_NOTIFICATION } from "../../state/mutation-types";
import Badge from "../ui/Badge.vue";
import SimpleButton from "../ui/SimpleButton.vue";
import {mapActions, mapStores} from "pinia";
import {useMainStore} from "../../state/store";
import {useMultiPostingStore} from "../../state/modules/multiPosting";
import {useNotificationsStore} from "../../state/modules/notifications";
import {useRepo} from "pinia-orm";
import Tooltip from "../navigation/Tooltip.vue";

export default {
    name: 'CampaignListItem',
    components: {
        Tooltip,
        SimpleButton,
        Badge,
        PopupModalConfirm,
        SvgIcon, CampaignChannels, CampaignMoreMenu, Currency},
    props: {
        hasMultiPostingSubscription: {
            type: Boolean,
            default: false,
        },
        campaign: {type: Object, required: true}
    },
    data: function () {
        return {
            deletingDraft: false,
            performingDeletion: false,
            takingCampaignOffline: false,
            takingChannelOffline: false,
            performingTakeCampaignOffline: false,
            performingTakeChannelOffline: false,
            accordionOpen: false,
            fetchingJobs: []
        }
    },
    computed: {
        ...mapStores(useMainStore, useMultiPostingStore),
        locale() {
            return this.mainStore.locale;
        },
        dateFormat() {
            return this.mainStore.dateFormat;
        },
        selectedItems() {
            return this.multiPostingStore.selectedItems;
        },
        form() {
            return this.multiPostingStore.form;
        },
        campaigns() {
            return this.multiPostingStore.campaigns;
        },
        accordionIcon() {
            return this.accordionOpen ? 'keyboard-arrow-up' : 'keyboard-arrow-down'
        },
        name() {
          return this.campaign.name && this.campaign.name.trim().length > 0 && this.campaign.name !== "default" ?
                     this.campaign.name : this.$t('multi-posting.draft_campaign');
        },
        status() {
            if (this.campaign.draft_mode) {
                return this.$t('campaigns.statuses.draft');
            }
            return this.$t(`multi-posting.campaigns.status.${this.campaign.status.replace(' ', '_')}`) || this.campaign.status
        },
        statusColor() {
            return this.campaign.draft_mode ? 'info'
                : this.campaign.status === 'online' ? 'success'
                    : this.campaign.status === 'in progress' ? 'warning'
                        : 'danger'
        },
        createdAt() {
            return this.format(this.campaign.created_at)
        },
        startDate() {
            return this.format(this.campaign.start_date)
        },
        duration() {
            const duration = this.extra().duration
            return duration ? duration : 0;
        },
        applications() {
            return this.campaign.applications;
        },
        timeLocale() {
            return this.locale === "en" ? "en-gb" : this.locale
        }
    },
    methods: {
        ...mapActions(useMultiPostingStore, [
            'duplicateCampaign',
            'deleteDraft',
            'cancelCampaign',
            'cancelCampaignProduct',
            'setDefaultStep',
            'loadCampaign',
            'setCreatingCampaign'
        ]),
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        fetchJob() {
            if (this.campaign.jobId &&
                !this.fetchingJobs.includes(this.campaign.jobId) &&
                !useRepo(Job).where('id', this.campaign.jobId).get().length
            ) {
                this.fetchingJobs.push(this.campaign.jobId);
                JobService.find(this.campaign.jobId).then((job) => {
                    return useRepo(Job).save(job)
                }).then(() => {
                    const index = this.fetchingJobs.indexOf(this.campaign.jobId);
                    this.fetchingJobs.splice(index, 1);
                });
            }
        },
        price(campaign) {
            let price = 0;
            if (campaign.draft_mode) {
                campaign.channels.forEach(channel => {
                    price += channel.price.amount;
                })
            } else {
                price = campaign.price
            }
            return price ? price : 0;
        },
        extra() {
            const extra = this.campaign.extra
            return extra ? extra : {}
        },
        format(date) {
            return date ? moment(date).locale(this.timeLocale).format("L") : "";
        },
        toggleAccordion() {
            this.accordionOpen = !this.accordionOpen
        },
        addNewFromThis(campaign) {
           //
        },
        viewAnalytics(campaign) {
            this.editCampaign({campaignId: campaign.id})
        },
        takeCampaignOffline() {
            this.performingTakeCampaignOffline = true;
            const successMessage = this.$t('multi-posting.take_offline.campaign.success');
            this.cancelCampaign(this.campaign.guid)
                .then(() => this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: successMessage
                }))
                .finally(() => {
                    this.takingCampaignOffline = false;
                    this.performingTakeCampaignOffline = false;
                    this.$emit('update-campaigns');
                });
        },
        takeOffline(channel) {
            this.takingChannelOffline = channel;
        },
        takeChannelOffline() {
            this.performingTakeChannelOffline = true;
            this.cancelCampaignProduct({campaignId: this.campaign.guid, campaignItemId: this.takingChannelOffline.id})
                .then(() => {
                    this.takingChannelOffline = false;
                    this.performingTakeChannelOffline = false;
                });
        },
        deleteDraftCampaign() {
            this.performingDeletion = true;
            const successMessage = this.$t('multi-posting.campaigns.delete_draft.success');
            this.deleteDraft(this.campaign.guid).then((result) => {
                this.deletingDraft = false;
                this.performingDeletion = false;
                if (result && result.success === "Campaign deleted") {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: successMessage,
                    });
                }
            });
        },
        getCampaignById(campaignId) {
            return this.campaigns.find((c) => c.id === campaignId);
        },
        editCampaign({campaignId}) {
            if (!!this.campaign.brockmeyer_old_partner) {
                this.accordionOpen = !this.accordionOpen;
                return;
            }
            campaignId = campaignId || this.campaign.id;
            const selectedCampaign = this.getCampaignById(campaignId);
            if (!selectedCampaign) {
                return
            }
            if (!selectedCampaign.editable) {
                this.setDefaultStep(4)
            }
            this.loadCampaign(selectedCampaign)
            this.setCreatingCampaign(true)
            this.addCampaignToRequest(selectedCampaign.guid)
        },
        addCampaignToRequest(guid) {
            const campaignId = guid
            this.$router.push({ query: { ...this.$route.query, campaignId }, replace: true });
        },
        duplicateCampaignAction() {
            this.duplicateCampaign(this.campaign.guid)
            .then((campaign) => {
                this.$emit('duplicated-campaign', campaign);
            });
        }
    }
}
</script>

<style scoped>
.fade-enter-active {
    transition: opacity .4s ease;
}
.fade-leave-active {
    transition: opacity .0s;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
